import { createTheme } from "@mantine/core";

import actionIcon from "../assets/theme/ActionIcon.module.css";
import anchor from "../assets/theme/Anchor.module.css";
import avatar from "../assets/theme/Avatar.module.css";
import badge from "../assets/theme/Badge.module.css";
import button from "../assets/theme/Button.module.css";
import card from "../assets/theme/Card.module.css";
import checkbox from "../assets/theme/Checkbox.module.css";
import combobox from "../assets/theme/Combobox.module.css";
import datePickerInput from "../assets/theme/DatePickerInput.module.css";
import dropzone from "../assets/theme/Dropzone.module.css";
import input from "../assets/theme/Input.module.css";
import inputWrapper from "../assets/theme/InputWrapper.module.css";
import menu from "../assets/theme/Menu.module.css";
import numberInput from "../assets/theme/NumberInput.module.css";
import pill from "../assets/theme/Pill.module.css";
import skeleton from "../assets/theme/Skeleton.module.css";
import tabs from "../assets/theme/Tabs.module.css";
import text from "../assets/theme/Text.module.css";
import themeIcon from "../assets/theme/ThemeIcon.module.css";
import timeline from "../assets/theme/Timeline.module.css";

import type { BadgeVariant, DefaultMantineColor, MantineColorsTuple } from "@mantine/core";

import "../assets/fonts/fonts.css";
import "../assets/styles/global.css";

const primary: MantineColorsTuple = [
  "#E8EBF7",
  "#D0D8EF",
  "#A1B0E0",
  "#7289D0",
  "#5B75C8",
  "#4361C1",
  "#2B4EB9",
  "#143AB1", // base
  "#102E8E",
  "#0E297C",
];

/* Dark Mode */
const primaryDark: MantineColorsTuple = [
  "#F9F9F9",
  "#F2F2F2",
  "#E6E6E6",
  "#CCCCCC",
  "#999999",
  "#666666",
  "#4D4D4D",
  "#333333", // base
  "#191919",
  "#000000",
];

const secondary: MantineColorsTuple = [
  "#E9E9E9",
  "#D3D3D3",
  "#A7A7A7",
  "#7A7A7A",
  "#646464",
  "#646464",
  "#383838",
  "#222222", // base
  "#222222",
  "#222222",
];

const danger: MantineColorsTuple = [
  "#FFF7F8",
  "#FFE9EB",
  "#FFD4D7",
  "#FFA9B0",
  "#FF7D88",
  "#FF5261",
  "#FF2739", // base
  "#CC1F2E",
  "#991722",
  "#661017",
  "#DC6565",
];

const warning: MantineColorsTuple = [
  "#FEFBF6",
  "#FFF8EB",
  "#FFF2D5",
  "#FFE6AA",
  "#FFD97E",
  "#FFCC4C",
  "#F3C759", // base
  "#C29F47",
  "#927735",
  "#615024",
];

const success: MantineColorsTuple = [
  "#F4FCF5",
  "#E1FDE7",
  "#C1F9CD",
  "#79F499",
  "#00ED62",
  "#00DA16",
  "#2FD543", // base
  "#1C8028",
  "#1C8028",
  "#13551B",
];

type ExtendedCustomColors =
  | "primary"
  | "secondary"
  | "danger"
  | "warning"
  | "success"
  | "primaryDark"
  | DefaultMantineColor;

type ExtendedBadgeVariants =
  | "light-green"
  | "light-red"
  | "light-yellow"
  | "light-gray"
  | BadgeVariant;

declare module "@mantine/core" {
  export interface MantineThemeColorsOverride {
    colors: Record<ExtendedCustomColors, MantineColorsTuple>;
  }

  export interface BadgeProps {
    variant?: ExtendedBadgeVariants;
  }
}

export const theme = createTheme({
  colors: {
    primary,
    secondary,
    danger,
    warning,
    success,
    primaryDark,
  },
  fontFamily: "'Outfit', sans-serif",
  primaryColor: "primary",
  primaryShade: 7,
  autoContrast: true,
  defaultRadius: "md",
  headings: {
    fontWeight: "500",
  },
  shadows: {
    md: "0 0 var(--mantine-spacing-sm) 0 rgba(0, 0, 0, 0.15)",
  },
  radius: {
    xxs: "calc(0.125rem * var(--mantine-scale))", // 2
    xs: "calc(0.25rem * var(--mantine-scale))", // 4
    sm: "calc(0.375rem * var(--mantine-scale))", // 6
    md: "calc(0.5rem * var(--mantine-scale))", // 8
    lg: "calc(1rem * var(--mantine-scale))", // 16
    xl: "calc(2rem * var(--mantine-scale))", // 32
  },
  spacing: {
    xxs: "calc(0.125rem * var(--mantine-scale))", // 2
    xs: "calc(0.25rem * var(--mantine-scale))", // 4
    sm: "calc(0.375rem * var(--mantine-scale))", // 6
    md: "calc(0.5rem * var(--mantine-scale))", // 8
    lg: "calc(1rem * var(--mantine-scale))", // 16
    xl: "calc(2rem * var(--mantine-scale))", // 32
  },
  components: {
    ActionIcon: {
      classNames: actionIcon,
      defaultProps: {
        variant: "light",
        radius: "xs",
        size: "lg", // 32
      },
    },
    Anchor: {
      classNames: anchor,
    },
    AppShell: {
      styles: {
        root: {
          backgroundColor: "light-dark(#F7F8FA, #000)",
        },
      },
    },
    Avatar: {
      classNames: avatar,
    },
    Badge: {
      defaultProps: {
        px: "var(--mantine-spacing-sm)",
        py: "var(--mantine-spacing-sm)",
        radius: "sm",
      },
      classNames: badge,
    },
    Button: {
      classNames: button,
      defaultProps: {
        radius: "sm",
      },
    },
    Card: {
      classNames: card,
      defaultProps: {
        padding: "lg",
        shadow: "md",
      },
    },
    Checkbox: {
      classNames: checkbox,
      defaultProps: {
        radius: "xs",
      },
    },
    Combobox: {
      classNames: combobox,
    },
    DatePickerInput: {
      classNames: datePickerInput,
    },
    Dropzone: {
      classNames: dropzone,
    },
    Grid: {
      defaultProps: {
        gutter: "lg",
      },
    },
    Group: {
      defaultProps: {
        gap: "lg",
      },
    },
    Input: {
      classNames: input,
    },
    InputWrapper: {
      classNames: inputWrapper,
      styles: {
        error: {
          color: "light-dark(var(--mantine-color-danger-7), var(--mantine-color-danger-10))",
        },
      },
    },
    NumberInput: {
      classNames: numberInput,
    },
    Menu: {
      classNames: menu,
      styles: {
        dropdown: {
          backgroundColor:
            "light-dark(var(--mantine-color-white), var(--mantine-color-primaryDark-8))",
        },
      },
    },
    Modal: {
      defaultProps: {
        padding: "calc(1.25rem * var(--mantine-scale))",
      },
      styles: {
        title: {
          color: "light-dark(var(--mantine-color-secondary-9), var(--mantine-color-secondary-2))",
        },
        close: {
          color: "light-dark(var(--mantine-color-secondary-9), var(--mantine-color-secondary-2))",
        },
      },
    },
    Notification: {
      styles: {
        title: {
          fontSize: "var(--mantine-font-size-xl)",
          fontWeight: "bold",
          color: "light-dark(var(--mantine-color-primaryDark-2), var(--mantine-color-secondary-9))",
        },
        description: {
          fontFamily: "Serif-Font",
          fontSize: "var(--mantine-font-size-md)",
          color: "light-dark(var(--mantine-color-primaryDark-2), var(--mantine-color-secondary-9))",
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflowWrap: "break-word",
        },
        root: {
          backgroundColor:
            "light-dark(var(--mantine-color-secondary-9), var(--mantine-color-primaryDark-2))",
          padding: "20px",
          maxWidth: "100%",
          width: "400px",
        },
        closeButton: {
          position: "absolute",
          top: "10px",
          right: "10px",
          color: "light-dark(var(--mantine-color-primaryDark-2), var(--mantine-color-secondary-9))",
          backgroundColor: "transparent",
        },
        icon: {
          display: "none",
        },
      },
    },
    Pill: {
      classNames: pill,
    },
    PillsInput: {
      classNames: input,
    },
    Radio: {
      classNames: checkbox,
    },
    Select: {
      classNames: combobox,
    },
    Skeleton: {
      classNames: skeleton,
      defaultProps: {
        animated: undefined,
      },
    },
    SimpleGrid: {
      defaultProps: {
        spacing: "lg",
      },
    },
    Stack: {
      defaultProps: {
        gap: "lg",
      },
    },
    Table: {
      defaultProps: {
        horizontalSpacing: "lg",
        striped: true,
        verticalSpacing: "md",
      },
      styles: {
        tfoot: {
          borderTop:
            "calc(var(--mantine-spacing-xxs) * var(--mantine-scale)) solid var(--mantine-color-secondary-1)",
        },
        tbody: {
          borderBottom:
            "calc(var(--mantine-spacing-xxs) * var(--mantine-scale)) solid var(--mantine-color-secondary-1)",
          borderTop:
            "calc(var(--mantine-spacing-xxs) * var(--mantine-scale)) solid var(--mantine-color-secondary-1)",
        },
        thead: {
          borderBottom:
            "calc(var(--mantine-spacing-xxs) * var(--mantine-scale)) solid var(--mantine-color-secondary-1)",
        },
        th: {
          color: "secondary.7",
        },
      },
    },
    Tabs: {
      classNames: tabs,
    },
    Text: {
      classNames: text,
    },
    TextInput: {
      styles: {
        label: {
          color: "light-dark(var(--mantine-color-dimmed), var(--mantine-color-gray-4))",
        },
      },
    },
    Textarea: {
      styles: {
        label: {
          color: "light-dark(var(--mantine-color-dimmed), var(--mantine-color-gray-4))",
        },
      },
    },
    ThemeIcon: {
      classNames: themeIcon,
      defaultProps: {
        variant: "light",
        radius: "xs",
        size: "lg", // 32
      },
    },
    Timeline: {
      classNames: timeline,
    },
  },
});
